import { useCallback, useMemo } from 'react';
import assignmentService from 'services/assignment';
import submissionService from 'services/submission';
import { shouldInvalidateFormative, shouldInvalidatePreview } from 'utils/assignmentInvalidation';

const getPreviewSubmissionsToDelete = (previewFiles = [], submissions = []) =>
    previewFiles
        .map((previewFile) =>
            submissions.find(
                (submission) =>
                    submission.files?.[0]?.key === previewFile.data.key ||
                    submission.files?.[0]?.key?.includes(previewFile.data.key)
            )
        )
        .filter(Boolean);

const useAssignmentDerivedDataCleanup = ({ previewSubmissions, setPreviewSubmissions, setStudentSubmissions }) => {
    const prepare = useCallback((previous, next) => {
        const previewInvalidated = Boolean(previous?.id && shouldInvalidatePreview(previous, next));
        const formativeInvalidated = Boolean(previous?.id && shouldInvalidateFormative(previous, next));
        const assignment = { ...next };
        if (previewInvalidated) {
            assignment.has_seen_preview = false;
            assignment.preview_files = (assignment.preview_files || []).map((file) => ({
                ...file,
                is_preview_up_to_date: false,
            }));
        }
        return { assignment, previewInvalidated, formativeInvalidated };
    }, []);

    const execute = useCallback(
        async ({ assignmentId, assignment, previewInvalidated, formativeInvalidated }) => {
            const cleanupErrors = [];
            if (formativeInvalidated) {
                try {
                    await Promise.all([
                        assignmentService.deleteAssignmentFormatives(assignmentId),
                        assignmentService.deleteAssignmentSubmissions(
                            assignmentId,
                            JSON.stringify({ type: 'formative' })
                        ),
                    ]);
                    setStudentSubmissions([]);
                } catch (error) {
                    cleanupErrors.push(error);
                }
            }
            if (previewInvalidated) {
                const submissionsToDelete = getPreviewSubmissionsToDelete(assignment.preview_files, previewSubmissions);
                try {
                    await Promise.all(submissionsToDelete.map(({ id }) => submissionService.deleteSubmission(id)));
                    const deletedIds = new Set(submissionsToDelete.map(({ id }) => id));
                    setPreviewSubmissions((current) => current.filter(({ id }) => !deletedIds.has(id)));
                } catch (error) {
                    cleanupErrors.push(error);
                }
            }
            return cleanupErrors;
        },
        [previewSubmissions, setPreviewSubmissions, setStudentSubmissions]
    );

    return useMemo(() => ({ execute, prepare }), [execute, prepare]);
};

export default useAssignmentDerivedDataCleanup;
